import { useFormik } from '@fourthwall/utils/dist/formik';
import React, { useMemo } from 'react';
import { useTiersQuery } from '@modules/Dashboard/Memberships/modules/Perks/hooks/useTiersQuery';
import { CommentsWrittenRule } from '../../components/CommentsWrittenRule';
import { LikesGivenRule } from '../../components/LikesGivenRule';
import { LikesReceivedRule } from '../../components/LikesReceivedRule';
import { SelectRule } from '../../components/SelectRule';
import { TierRule } from '../../components/TierRule';
import { validationSchema } from './consts';
import { FormFields } from './types';
export const useBadgeForm = ({ initialValues, onSubmit }) => {
    const { tiersQuery } = useTiersQuery();
    const form = useFormik({
        initialValues: initialValues || {
            [FormFields.text]: '',
            [FormFields.emoji]: {
                type: 'Emoji',
                emojiIcon: '😀',
            },
            [FormFields.borderColor]: undefined,
            [FormFields.backgroundColor]: 'rgba(0,0,0,0)',
            [FormFields.textColor]: undefined,
            [FormFields.canBeEarnedByMembers]: true,
            [FormFields.removeWhenRulesNoLongerMet]: true,
            [FormFields.giveToSelectedMembers]: false,
            [FormFields.rules]: [],
            [FormFields.manuallyAssignedSupporters]: [],
            [FormFields.memberStreakBadges]: [],
        },
        onSubmit,
        validationSchema,
        enableReinitialize: true,
    });
    const availableTiers = useMemo(() => {
        if (!tiersQuery.data)
            return [];
        return tiersQuery.data.tiers.map((tier) => ({
            label: tier.name,
            value: tier.id.toString(),
            key: tier.id.toString(),
            disabled: !!form.values.rules.find((rule) => rule.type === 'Tier' && rule.tierId === tier.id),
        }));
    }, [tiersQuery.data, form.values.rules]);
    const availableRules = useMemo(() => {
        const options = [
            {
                label: 'Being a member of a selected tier',
                value: 'Tier',
                key: 'Tier',
                disabled: availableTiers.length === 0,
            },
            {
                label: 'Number of comments written',
                value: 'CommentsWritten',
                key: 'CommentsWritten',
                disabled: form.values.rules.some((rule) => rule.type === 'CommentsWritten'),
            },
            {
                label: 'Number of likes given',
                value: 'LikesGiven',
                key: 'LikesGiven',
                disabled: form.values.rules.some((rule) => rule.type === 'LikesGiven'),
            },
            {
                label: 'Number of likes received',
                value: 'LikesReceived',
                key: 'LikesReceived',
                disabled: form.values.rules.some((rule) => rule.type === 'LikesReceived'),
            },
        ];
        return options;
    }, [form.values, availableTiers]);
    const getRuleComponent = (rule) => {
        switch (rule.type) {
            case 'CommentsWritten':
                return React.createElement(CommentsWrittenRule, { form: form, options: availableRules });
            case 'LikesGiven':
                return React.createElement(LikesGivenRule, { form: form, options: availableRules });
            case 'LikesReceived':
                return React.createElement(LikesReceivedRule, { form: form, options: availableRules });
            case 'Tier':
                return React.createElement(TierRule, { form: form, options: availableRules, tiers: availableTiers, rule: rule });
            case 'EmptyRule':
                return React.createElement(SelectRule, { form: form, options: availableRules });
            default:
                return React.createElement("div", null);
        }
    };
    return {
        form,
        availableRules,
        hasEmptyRule: form.values.rules.some((rule) => rule.type === 'EmptyRule'),
        hasAnyRules: form.values.rules.length > 0,
        hasAvailableRules: availableRules.length > 0,
        getRuleComponent,
    };
};
